import { HEADERS } from 'apisauce';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { ApplicationAuthentication } from '~domain/model/account/application-authentication';
import { Answer } from '~domain/model/application/answer';
import { Application, ApplicationsMetrics } from '~domain/model/application/application';
import { ApplicationAttachmentDownloadUrl } from '~domain/model/application/attachment/application-attachment-download-url';
import { CarDescription } from '~domain/model/application/car/car-description';
import { CheckoutOptions } from '~domain/model/application/checkout-options';
import { Address } from '~domain/model/application/home/address';
import { PropertyCharacteristics } from '~domain/model/application/home/property-characteristics';
import InitializeApplicationOptions from '~domain/model/application/initialize-application-options';
import { PolicyIssuedOptions } from '~domain/model/application/policy-issued-options';
import { ProcessCoverageOptions } from '~domain/model/application/process-coverage-options';
import { ApplicationSearchFilters } from '~domain/model/core/application-search-filters';
import { ProductType } from '~domain/model/core/product-type';
import { AutopilotJourneyContext } from '~domain/model/email/autopilot-journey-context';
import { AutopilotJourney } from '~domain/model/external/autopilot/autopilot-journey';
import { FlinksConnector } from '~domain/model/external/flinks/flinks-connector';
import { ShortenLinkOptions } from '~domain/model/shorten-link-options/shorten-link-options';
import { SmsOptions } from '~domain/model/sms/sms-options';
import { SmsType } from '~domain/model/sms/sms-type';
import { BaseClient, DEFAULT_JSON_API_HEADERS } from './base-client';
import { RequestErrorHandler } from './request-error-handlers/base-request-error-handler';

export class ApplicationClient extends BaseClient {
  constructor(baseUrl: string, headers: HEADERS = {}, requestErrorHandler?: RequestErrorHandler) {
    super(baseUrl, { ...DEFAULT_JSON_API_HEADERS, ...headers }, requestErrorHandler);
  }

  public addOrReplaceAnswer$(accessToken: string, answer: Answer, applicationId: string): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.update$(`/${applicationId}/answers`, answer);
  }

  public authorize$(apiKey: string, options: InitializeApplicationOptions): Observable<ApplicationAuthentication> {
    // Set a temporary header that will be removed after the request
    const headers = { 'x-api-key': apiKey };

    return this.create$<ApplicationAuthentication>('/authorize', options, {}, headers);
  }

  public checkoutApplication$(
    accessToken: string,
    id: string,
    checkoutOptions: CheckoutOptions,
  ): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.update$(`/${id}/checkout`, checkoutOptions);
  }

  public copyApplication$(
    accessToken: string,
    applicationId: string,
    productType: ProductType,
  ): Observable<ApplicationAuthentication> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.create$<ApplicationAuthentication>(`/${applicationId}/${productType}`);
  }

  public deleteApplication(accessToken: string, applicationId: string): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.delete$<Application>(`/${applicationId}`).toPromise();
  }

  public duplicateApplication(accessToken: string, applicationId: string): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.create$<Application>(`/${applicationId}`).toPromise();
  }

  public generateAuthorizationToken$(accessToken: string, applicationId: string): Observable<string> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.create$<string>(`/${applicationId}/authorize`);
  }

  public getApplicationById$(accessToken: string, applicationId: string): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<Application>(`/${applicationId}`);
  }

  public getApplicationMetrics(accessToken: string): Promise<ApplicationsMetrics> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<ApplicationsMetrics>(`/metrics`).toPromise();
  }

  public getByFilters(
    accessToken: string,
    pageIndex: number,
    filters: ApplicationSearchFilters,
  ): Promise<Application[]> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<Application[]>(``, {
      application_id: filters.applicationId,
      first_name: filters.firstName,
      last_name: filters.lastName,
      pages_number: pageIndex,
      status_id: !isNil(filters.status) ? filters.status.id : undefined,
    }).toPromise();
  }

  public getCarDescriptionFromVin(accessToken: string, applicationId: string, vin: string): Promise<CarDescription[]> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<CarDescription[]>(`${applicationId}/car/vin/${vin}`).toPromise();
  }

  public getCarDescriptions$(
    accessToken: string,
    applicationId: string,
    year: number,
    make?: string,
    model?: string,
    language?: string,
  ): Observable<CarDescription> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    // Construct searched criterias if they are provided
    const queryParam: string[] = [`modelYear=${year}`];
    if (!isNil(make)) queryParam.push(`make=${make}`);
    if (!isNil(model)) queryParam.push(`model=${model}`);
    if (!isNil(language)) queryParam.push(`language=${language}`);

    return this.list$(`${applicationId}/car/descriptions?${queryParam.join('&')}`);
  }

  public getCarModelYears(accessToken: string, applicationId: string): Promise<number[]> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.fetch$<number[]>(`${applicationId}/car/modelYear`).toPromise();
  }

  // TODO: not used?
  public getCheckoutApplicationCopy$(accessToken: string, applicationId: string): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<Application>(`/${applicationId}/copy`);
  }

  public getPadPayment(accessToken: string, applicationId: string): Promise<Answer> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.fetch$<Answer>(`/${applicationId}/answer/pad-payment`).toPromise();
  }

  public getPropertyCharacteristics$(
    accessToken: string,
    address: Address,
    applicationId: string,
  ): Observable<PropertyCharacteristics> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    // Map address fields to url parameters
    const urlParameters = {
      city: address.city,
      lat: undefined,
      long: undefined,
      province: address.province,
      street_name: address.streetName,
      street_number: address.streetNumber,
    };

    if (address.coordinates && address.coordinates.latitude && address.coordinates.longitude) {
      urlParameters.lat = address.coordinates.latitude;
      urlParameters.long = address.coordinates.longitude;
    }

    return this.fetch$(`${applicationId}/property_characteristics`, urlParameters);
  }

  public policyIssued(accessToken: string, applicationId: string, options: PolicyIssuedOptions): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.updatePartially$<Application>(`/${applicationId}/policyNumbers`, options).toPromise();
  }

  public processCoverage(
    accessToken: string,
    applicationId: string,
    options: ProcessCoverageOptions,
  ): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.updatePartially$<Application>(`/${applicationId}/coverage`, options).toPromise();
  }

  public runAllApplicationPostOperations(accessToken: string, applicationId: string): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.update$<Application>(`/${applicationId}/postOperations/all`).toPromise();
  }

  public send2Close$(accessToken: string, applicationId: string): Observable<void> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.update$<void>(`/${applicationId}/close`);
  }

  public shortenLink$(
    accessToken: string,
    applicationId: string,
    shortenLinkOptions: ShortenLinkOptions,
  ): Observable<string> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    // shorten link options contains the originalURL and an optional ttl parameter expression expiry of shortened link as unix timestamp
    return this.create$<string>(`/${applicationId}/short/link`, shortenLinkOptions, null);
  }

  public triggerAutopilotJourney$<
    TJourney extends AutopilotJourney,
    TContext extends AutopilotJourneyContext<TJourney>,
  >(accessToken: string, applicationId: string, journeyId: TJourney, context?: TContext): Observable<void> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.create$<void>(`/${applicationId}/autopilot/${journeyId}`, context);
  }

  public triggerSms$(
    accessToken: string,
    applicationId: string,
    smsType: SmsType,
    smsOptions: SmsOptions,
  ): Observable<void> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return this.create$<void>(`/${applicationId}/sms/${smsType}`, smsOptions);
  }

  public updateApplicationFields$(
    accessToken: string,
    applicationId: string,
    updatedApplication: Partial<Application>,
  ): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.update$(`/${applicationId}`, updatedApplication);
  }

  public updateFlinksPayment$(
    accessToken: string,
    applicationId: string,
    flinksConnector: FlinksConnector,
  ): Observable<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.update$(`/${applicationId}/answers/flinks-payment`, flinksConnector);
  }

  public updatePadPayment$(accessToken: string, applicationId: string, userAnswer: Answer): Observable<void> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.update$(`/${applicationId}/answers/pad-payment`, userAnswer);
  }

  deleteRedirections(accessToken: string, applicationId: string): Promise<Application> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    return this.delete$<Application>(`/${applicationId}/redirection`).toPromise();
  }

  public getAttachmentDownloadUrl(
    accessToken: string,
    applicationId: string,
    applicationAttachmentId: string,
  ): Promise<ApplicationAttachmentDownloadUrl> {
    return this.fetch$<ApplicationAttachmentDownloadUrl>(
      `/${applicationId}/attachment/${applicationAttachmentId}/download-url`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    ).toPromise();
  }
}
